<script>
import BetaForm from '@/components/BetaForm.vue'
import EvidenceFusion from '@/components/EvidenceFusion.vue'
import HeatmapFigure from '@/components/HeatmapFigure.vue'
import SpectrumFigure from '@/components/SpectrumFigure.vue'
import VerdictScale from '@/components/VerdictScale.vue'
import { COMPARISON, COMPARISON_FIELDS, ROADMAP } from '@/util/content/marketing.js'

export default {
    name: 'HomeView',
    components: { BetaForm, EvidenceFusion, HeatmapFigure, SpectrumFigure, VerdictScale },
    data() {
        return {
            comparison: COMPARISON,
            comparisonFields: COMPARISON_FIELDS,
            roadmap: ROADMAP,
        }
    },
}
</script>

<template>
    <div>
        <!-- Hero -->
        <section class="ps-hero">
            <b-container>
                <b-row class="align-items-center">
                    <b-col lg="6" class="ps-hero__copy">
                        <h1 class="ps-display">Forensic detection of AI-generated and AI-edited images</h1>
                        <p class="ps-lede mt-4 mb-4 mb-md-5">
                            PolySigma is an image analysis API built on signal processing, statistical modeling and deep learning. It determines whether an image came from a camera, was edited after capture, or was produced in whole or in part by a generative model, and it identifies the regions
                            responsible.
                        </p>
                        <div class="d-flex flex-wrap ps-hero__actions">
                            <b-button :to="{ name: 'home', hash: '#beta' }" variant="primary" size="lg">Request beta access</b-button>
                            <b-button :to="{ name: 'home', hash: '#methods' }" variant="outline-light" size="lg">Read the methods</b-button>
                        </div>
                    </b-col>
                    <b-col lg="6" xl="5" offset-xl="1" class="ps-hero__figure">
                        <spectrum-figure />
                    </b-col>
                </b-row>
            </b-container>
        </section>

        <!-- The problem -->
        <section id="problem" class="ps-section">
            <b-container>
                <b-row>
                    <b-col lg="5" class="mb-4 mb-lg-0">
                        <h2 class="ps-heading mb-0">The limits of visual inspection</h2>
                    </b-col>
                    <b-col lg="6" offset-lg="1" class="ps-body">
                        <p class="ps-lede mb-4">Diffusion models and GANs now produce photographically convincing images, and generative editing tools can replace a face, an object or a background in an existing photo within seconds.</p>
                        <p class="mb-4">
                            People are poor judges of the results. In a 2022 study, participants asked to tell StyleGAN2 faces from photographs of real people were correct 48.2% of the time, slightly below chance, and rated the synthetic faces as more trustworthy on average.<sup
                                ><a href="#ref-1" class="ps-ref">1</a></sup
                            >
                            Generators have improved considerably since then.
                        </p>
                        <p class="mb-4">Generated and edited images still differ from camera captures in ways that can be measured. The differences are found in pixel statistics, frequency content, compression history and file metadata, and they persist in images that look entirely natural.</p>
                        <p id="ref-1" class="ps-reference mb-0">1. Nightingale, S. J. and Farid, H. (2022). AI-synthesized faces are indistinguishable from real faces and more trustworthy. <i>Proceedings of the National Academy of Sciences</i>, 119(8), e2120481119.</p>
                    </b-col>
                </b-row>
            </b-container>
        </section>

        <!-- Approach -->
        <section id="approach" class="ps-section ps-section--ink">
            <b-container>
                <b-row>
                    <b-col lg="5" class="mb-4 mb-lg-0">
                        <h2 class="ps-heading mb-0">Approach</h2>
                    </b-col>
                    <b-col lg="6" offset-lg="1" class="ps-body">
                        <p class="ps-lede mb-4">PolySigma is a detection system. It doesn’t generate or alter content, and it isn’t built on a general-purpose AI model.</p>
                        <p class="mb-4">
                            The analysis draws on several disciplines. Digital forensics supplies tests on metadata and file structure. Signal processing supplies measurements of sensor noise, compression and frequency content. Statistical modeling combines the individual results and calibrates the
                            confidence score. Deep learning is used where trained classifiers outperform hand-designed tests.
                        </p>
                        <p class="mb-0">Most of these measurements have a known physical or statistical explanation. When a classification is questioned, the evidence behind it can be described and shown on the image.</p>
                    </b-col>
                </b-row>
            </b-container>
        </section>

        <!-- Methods -->
        <section id="methods" class="ps-section">
            <b-container>
                <b-row class="mb-5">
                    <b-col lg="5">
                        <h2 class="ps-heading">Methods</h2>
                    </b-col>
                    <b-col lg="6" offset-lg="1">
                        <p class="ps-lede mb-0">Each test is unreliable on its own. PolySigma runs four independent analyses on every image and combines their scores into one result. The Σ in the name refers to that sum.</p>
                    </b-col>
                </b-row>

                <evidence-fusion />

                <b-row class="ps-equation-block">
                    <b-col lg="5">
                        <p class="ps-equation mb-0" aria-label="p of synthetic equals sigma of the sum over i of w i times s i, plus b">
                            <i>p</i>(synthetic) = <i>σ</i>(&thinsp;<span class="ps-equation__sum">Σ</span><sub><i>i</i></sub> <i>w</i><sub><i>i</i></sub
                            ><i>s</i><sub><i>i</i></sub> + <i>b</i>&thinsp;)
                        </p>
                    </b-col>
                    <b-col lg="6" offset-lg="1">
                        <p class="text-muted mb-0">
                            Each analysis <i>i</i> produces a score <i>s</i><sub><i>i</i></sub
                            >. Its weight <i>w</i><sub><i>i</i></sub> is set by how accurate that analysis has been on held-out images, and the logistic function <i>σ</i> maps the weighted sum to a probability. When the analyses disagree, the probability lands nearer the middle of the scale and the
                            reported confidence is lower.
                        </p>
                    </b-col>
                </b-row>
            </b-container>
        </section>

        <!-- Classification -->
        <section id="classification" class="ps-section ps-section--ink">
            <b-container>
                <b-row class="mb-5">
                    <b-col lg="5">
                        <h2 class="ps-heading">Classification</h2>
                    </b-col>
                    <b-col lg="6" offset-lg="1">
                        <p class="ps-lede mb-4">Every image is assigned one of four classes, ordered from camera capture to fully synthetic, with a confidence score from 0 to 100.</p>
                        <p class="mb-0">Conventional edits are kept separate from generative ones because cropping, color correction and retouching are routine in genuine photographs. Treating them as evidence of AI would make the false positive rate on real images unacceptably high.</p>
                    </b-col>
                </b-row>
                <verdict-scale />
            </b-container>
        </section>

        <!-- Localization -->
        <section id="localization" class="ps-section">
            <b-container>
                <b-row class="align-items-center">
                    <b-col md="6" lg="5" class="mb-5 mb-md-0">
                        <heatmap-figure />
                    </b-col>
                    <b-col md="6" offset-lg="1">
                        <h2 class="ps-heading">Localization</h2>
                        <p class="ps-lede mb-4">Images classified as AI-edited or AI-generated also receive a heatmap, which shows how strongly each region of the image contributed to the classification.</p>
                        <p class="mb-3">In an AI-edited image the heatmap usually separates the altered region from the rest of the frame. In a fully generated image the evidence is typically spread across the whole picture.</p>
                        <p class="mb-0">The heatmap is computed after the classification and delivered with the same result. It lets a reviewer check a classification against the image itself.</p>
                    </b-col>
                </b-row>
            </b-container>
        </section>

        <!-- Maintenance -->
        <section id="maintenance" class="ps-section ps-section--ink">
            <b-container>
                <b-row class="mb-5">
                    <b-col lg="5">
                        <h2 class="ps-heading">No model to train or maintain</h2>
                    </b-col>
                    <b-col lg="6" offset-lg="1">
                        <p class="ps-lede mb-0">A detector’s accuracy decays as new generators are released, because each new architecture leaves somewhat different traces. Keeping one accurate means collecting new data, retraining and revalidating on a continuing basis.</p>
                    </b-col>
                </b-row>
                <b-row class="mb-5">
                    <b-col lg="5" class="ps-body mb-4 mb-lg-0">
                        <p class="mb-0">A detector trained in-house performs well on the generators in its training set and worse on those released afterward. Nothing fails visibly when that happens. Fewer synthetic images are caught, and the gap is usually discovered well after it opened.</p>
                    </b-col>
                    <b-col lg="6" offset-lg="1" class="ps-body">
                        <p class="mb-0">PolySigma carries that work. The classes, the confidence scale and the response format stay fixed while the models underneath are retrained or replaced, so an integration needs no changes when they are.</p>
                    </b-col>
                </b-row>
                <b-table :items="comparison" :fields="comparisonFields" stacked="md" class="ps-compare mb-0" thead-class="ps-compare__head">
                    <template #cell(concern)="row">
                        <span class="ps-compare__concern">{{ row.value }}</span>
                    </template>
                    <template #cell(polysigma)="row">
                        <span class="ps-compare__ours">{{ row.value }}</span>
                    </template>
                </b-table>
            </b-container>
        </section>

        <!-- Integration + roadmap -->
        <section id="integration" class="ps-section">
            <b-container>
                <b-row class="mb-5">
                    <b-col lg="5">
                        <h2 class="ps-heading">Integration</h2>
                    </b-col>
                    <b-col lg="6" offset-lg="1">
                        <p class="ps-lede mb-0">PolySigma is a REST API. An image is submitted in one request and its result is retrieved by ID.</p>
                    </b-col>
                </b-row>
                <b-row class="mb-5">
                    <b-col md="4" class="ps-point">
                        <h3 class="ps-title">Asynchronous processing</h3>
                        <p class="text-muted mb-0">Submitting an image returns an ID immediately, so the calling service never blocks on analysis. The classification is usually ready within seconds and the heatmap follows.</p>
                    </b-col>
                    <b-col md="4" class="ps-point">
                        <h3 class="ps-title">Retained results</h3>
                        <p class="text-muted mb-0">Each result stays available under its ID after processing, including the class, the confidence score and the heatmap, for later review or audit.</p>
                    </b-col>
                    <b-col md="4" class="ps-point">
                        <h3 class="ps-title">Further media types</h3>
                        <p class="text-muted mb-0">Video and audio analysis are in development. Both will use the same API and the same four classes as images.</p>
                    </b-col>
                </b-row>
                <ol class="ps-roadmap list-unstyled mb-0">
                    <li v-for="stage in roadmap" :key="stage.name" class="ps-roadmap__stage" :class="{ 'is-current': stage.current }">
                        <span class="ps-roadmap__node" aria-hidden="true"></span>
                        <h3 class="ps-title mb-1">{{ stage.name }}</h3>
                        <p class="small text-muted mb-0">{{ stage.state }}</p>
                    </li>
                </ol>
            </b-container>
        </section>

        <!-- Beta signup -->
        <section id="beta" class="ps-section ps-section--ink">
            <b-container>
                <b-row>
                    <b-col lg="5" class="mb-5 mb-lg-0">
                        <h2 class="ps-heading">Beta program</h2>
                        <p class="ps-lede mb-4">The image analysis beta is open to a limited number of organizations that process images at volume.</p>
                        <ul class="ps-perks list-unstyled mb-0">
                            <li>API access to classifications, confidence scores and heatmaps</li>
                            <li>Direct contact with the engineering team</li>
                            <li>Early access to video and audio analysis</li>
                        </ul>
                    </b-col>
                    <b-col lg="6" offset-lg="1">
                        <beta-form />
                    </b-col>
                </b-row>
            </b-container>
        </section>
    </div>
</template>

<style scoped lang="scss">
// --- Hero ---------------------------------------------------------------
.ps-hero {
    position: relative;
    overflow: hidden;
    padding-top: clamp(2.5rem, 8vw, 4rem);
    padding-bottom: clamp(4.5rem, 10vw, 6rem);
}

.ps-hero .ps-display {
    font-size: clamp(2.75rem, 12vw, 5rem);
    line-height: 0.92;
}

.ps-hero__actions {
    gap: 0.75rem;
}

.ps-hero__figure {
    margin-top: 3.5rem;
}

@media (max-width: 575.98px) {
    .ps-hero__actions .btn {
        flex: 1 1 100%;
    }
}

@media (min-width: 992px) {
    .ps-hero {
        display: flex;
        align-items: center;
        min-height: calc(100vh - 4.5rem);
        min-height: calc(100svh - 4.5rem);
    }

    .ps-hero > .container {
        width: 100%;
    }

    .ps-hero__figure {
        margin-top: 0;
    }
}

// --- References -------------------------------------------------------------
.ps-ref {
    color: var(--ps-real-text);
    font-weight: 600;
}

.ps-reference {
    padding-top: 1rem;
    border-top: 1px solid var(--ps-rule);
    font-size: 0.875rem;
    color: var(--ps-haze);
}

// --- Methods --------------------------------------------------------------
.ps-equation-block {
    margin-top: 3.5rem;
    align-items: center;
}

.ps-equation {
    font-size: clamp(1.5rem, 4.5vw, 2.25rem);
    font-weight: 400;
    line-height: 1.4;
    margin-bottom: 1.5rem !important;
    white-space: nowrap;
}

.ps-equation__sum {
    font-size: 1.35em;
    font-weight: 700;
    color: var(--ps-real-text);
}

@media (min-width: 992px) {
    .ps-equation {
        margin-bottom: 0 !important;
    }
}

// --- Maintenance comparison ---------------------------------------------------
.ps-compare {
    color: var(--ps-frost);
}

.ps-compare ::v-deep th,
.ps-compare ::v-deep td {
    padding: 1.15rem 1.5rem 1.15rem 0;
    border-color: var(--ps-rule);
    vertical-align: top;
}

.ps-compare ::v-deep .ps-compare__head th {
    border-top: 0;
    border-bottom: 1px solid var(--ps-rule);
    font-weight: 700;
    color: var(--ps-haze);
}

.ps-compare ::v-deep td {
    color: var(--ps-haze);
}

.ps-compare__concern {
    font-weight: 700;
    color: var(--ps-frost);
}

.ps-compare__ours {
    font-weight: 600;
    color: var(--ps-frost);
}

// Phones: Bootstrap-Vue stacks each row into labeled cells; tighten them
// and let the concern name read as the row's heading.
@media (max-width: 767.98px) {
    .ps-compare ::v-deep tr {
        border-top: 1px solid var(--ps-rule);
        padding: 1.25rem 0;
    }

    .ps-compare ::v-deep td {
        border: 0;
        padding: 0.35rem 0;
    }

    // Label above its value instead of Bootstrap-Vue's 40/60 side-by-side
    .ps-compare.table.b-table ::v-deep tbody > tr > [data-label] > div {
        display: block;
        float: none;
        width: auto;
        padding-left: 0;
    }

    .ps-compare.table.b-table ::v-deep tbody > tr > [data-label]::before {
        float: none;
        display: block;
        width: auto;
        padding: 0 0 0.15rem;
        font-weight: 600;
        color: var(--ps-real-text);
        text-align: left;
    }

    .ps-compare ::v-deep td[aria-colindex='1']::before {
        display: none;
    }

    .ps-compare ::v-deep td[aria-colindex='1'] > div {
        width: 100%;
        font-size: 1.25rem;
    }
}

// --- Integration ----------------------------------------------------------------
.ps-point {
    padding-top: 1.5rem;
    padding-bottom: 2rem;
}

// Rule drawn inside the gutters so it lines up with the text, not the column edge
.ps-point::before {
    content: '';
    position: absolute;
    top: 0;
    left: 16px;
    right: 16px;
    border-top: 1px solid var(--ps-rule);
}

.ps-roadmap {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    position: relative;
}

.ps-roadmap::before {
    content: '';
    position: absolute;
    top: 0.5rem;
    left: 0;
    right: 0;
    height: 2px;
    background: var(--ps-rule);
}

.ps-roadmap__stage {
    position: relative;
    padding-top: 2rem;
    padding-right: 1rem;
}

.ps-roadmap__node {
    position: absolute;
    top: 0;
    left: 0;
    width: 1.125rem;
    height: 1.125rem;
    border-radius: 50%;
    background: var(--ps-abyss);
    border: 2px solid #3f6080;
}

.ps-roadmap__stage.is-current .ps-roadmap__node {
    background: var(--ps-volt);
    border-color: var(--ps-volt);
    box-shadow: 0 0 0 0.375rem rgba(27, 154, 255, 0.2);
}

.ps-roadmap__stage.is-current .ps-title {
    color: var(--ps-real-text);
}

// --- Beta perks ---------------------------------------------------------------
.ps-perks li {
    position: relative;
    padding: 0.85rem 0 0.85rem 1.75rem;
    border-top: 1px solid var(--ps-rule);
}

.ps-perks li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 1.5rem;
    width: 0.875rem;
    height: 0.25rem;
    border-radius: 999px;
    background: var(--ps-volt);
}
</style>
